import React, { ChangeEvent, ReactNode } from 'react';

import { MenuItem, TextField } from '@material-ui/core';

import { useStyles } from './Themable.hooks';
import TranslationHelper from '../../helpers/TranslationHelper';
import { ListItemIcon, ListItemText } from '@mui/material';

interface IOwnProps<T extends string | number> {
    name: string;
    value: T;
    overrideClass?: any;
    ids?: T[];
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    translateContext?: string;
    renderOptions?: () => ReactNode;
    disabled?: boolean;
    iconMap?: Record<T, ReactNode>;
}

interface IRenderValueProps<T extends string | number> {
    value: T;
    iconMap?: Record<T, ReactNode>;
    translateContext: string;
}

function SelectedOption<T extends string>({
    value,
    iconMap,
    translateContext,
}: IRenderValueProps<T>) {
    const classes = useStyles();
    return (
        <div className={classes.valueWrapper}>
            {iconMap?.[value] && (
                <span className={classes.valueIcon}>
                    {React.isValidElement(iconMap[value])
                        ? React.cloneElement(
                              iconMap[value] as React.ReactElement,
                              { color: 'inherit' }
                          )
                        : iconMap[value]}
                </span>
            )}
            {TranslationHelper.translate(value, translateContext)}
        </div>
    );
}

const SelectField = <T extends string>({
    name,
    value,
    overrideClass,
    handleChange,
    ids,
    label,
    translateContext = 'wx',
    renderOptions,
    disabled,
    iconMap,
}: IOwnProps<T>) => {
    const themableClasses = useStyles();
    const classes = overrideClass ?? themableClasses;

    const defaultRenderOptions = () => {
        return ids?.map((id) => {
            return (
                <MenuItem key={id} value={id} className={classes.menuItem}>
                    {iconMap?.[id] && (
                        <ListItemIcon className={classes.listItemIcon}>
                            {iconMap[id]}
                        </ListItemIcon>
                    )}
                    <ListItemText>
                        {TranslationHelper.translate(id, translateContext)}
                    </ListItemText>
                </MenuItem>
            );
        });
    };
    return (
        <TextField
            className={classes.select}
            name={name}
            select
            label={TranslationHelper.translate(label || '')}
            variant="outlined"
            disabled={disabled}
            value={value}
            onChange={handleChange}
            classes={{ root: classes.root }}
            SelectProps={{
                classes: { root: classes.root, icon: classes.icon },
                ...(iconMap && {
                    renderValue: (selected: unknown) => (
                        <SelectedOption
                            value={selected as T}
                            iconMap={iconMap}
                            translateContext={translateContext}
                        />
                    ),
                }),
            }}
            size="small"
        >
            {renderOptions ? renderOptions() : defaultRenderOptions()}
        </TextField>
    );
};

export default SelectField;
